import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AddPassiveModal from "./AddPassiveModal";
import FeatPickerModal from "./FeatPickerModal";
import PassivesSkillBox from "./PassiveSkillBox";
import { isAdvancedRogueTalentOption } from "../../../calculations/reconcileCharacterRules";
import { CORE_FEATS } from "../../../catalogs/featsCatalog";
import createId from "../../../utils/createId";
import { getCharacterFeature, getClassFeature } from "../../../catalogs/characterFeaturesCatalog";
import { getRaceRules } from "../../../catalogs/raceRulesCatalog";
import { getRussianPassiveReference } from "../../../catalogs/passiveReferenceCatalog";
import { systemLabelKey } from "../../../catalogs/characterSystemCatalog";

const featCatalogById = new Map(CORE_FEATS.map((feat) => [feat.id, feat]));
const CLASS_REFERENCE_PAGES = {
  en: {
    barbarian: "https://www.aonprd.com/Classes.aspx?Class=Barbarian",
    bard: "https://www.aonprd.com/Classes.aspx?Class=Bard",
    cleric: "https://www.aonprd.com/Classes.aspx?Class=Cleric",
    druid: "https://www.aonprd.com/Classes.aspx?Class=Druid",
    fighter: "https://www.aonprd.com/Classes.aspx?Class=Fighter",
    monk: "https://www.aonprd.com/Classes.aspx?Class=Monk",
    paladin: "https://www.aonprd.com/Classes.aspx?Class=Paladin",
    ranger: "https://www.aonprd.com/Classes.aspx?Class=Ranger",
    rogue: "https://www.aonprd.com/Classes.aspx?Class=Rogue",
    sorcerer: "https://www.aonprd.com/Classes.aspx?Class=Sorcerer",
    wizard: "https://www.aonprd.com/Classes.aspx?Class=Wizard",
  },
  ru: {
    barbarian: "https://pathfinder.family/class/barbarian",
    bard: "https://pathfinder.family/class/bard",
    cleric: "https://pathfinder.family/class/cleric",
    druid: "https://pathfinder.family/class/druid",
    fighter: "https://pathfinder.family/class/fighter",
    monk: "https://pathfinder.family/class/monk",
    paladin: "https://pathfinder.family/class/paladin",
    ranger: "https://pathfinder.family/class/ranger",
    rogue: "https://pathfinder.family/class/rogue",
    sorcerer: "https://pathfinder.family/class/sorcerer",
    wizard: "https://pathfinder.family/class/wizard",
  },
};

const RACE_REFERENCE_PAGES = {
  en: {
    human: "https://www.aonprd.com/RacesDisplay.aspx?ItemName=Human",
    elf: "https://www.aonprd.com/RacesDisplay.aspx?ItemName=Elf",
    dwarf: "https://www.aonprd.com/RacesDisplay.aspx?ItemName=Dwarf",
    gnome: "https://www.aonprd.com/RacesDisplay.aspx?ItemName=Gnome",
    halfling: "https://www.aonprd.com/RacesDisplay.aspx?ItemName=Halfling",
    "half-elf": "https://www.aonprd.com/RacesDisplay.aspx?ItemName=Half-Elf",
    "half-orc": "https://www.aonprd.com/RacesDisplay.aspx?ItemName=Half-Orc",
  },
  ru: {
    human: "https://pathfinder.family/race/human",
    elf: "https://pathfinder.family/race/elf",
    dwarf: "https://pathfinder.family/race/dwarf",
    gnome: "https://pathfinder.family/race/gnome",
    halfling: "https://pathfinder.family/race/halfling",
    "half-elf": "https://pathfinder.family/race/halfElf",
    "half-orc": "https://pathfinder.family/race/halfOrc",
  },
};

const buildTextFragment = (name, description) => {
  const normalizedName = (name || "").trim();
  const normalizedDescription = (description || "").replace(/\s+/g, " ").trim();

  if (!normalizedName || !normalizedDescription) return "";

  const sentencePrefix = normalizedDescription
    .split(/[.!?]/)[0]
    .trim()
    .slice(0, 140)
    .trim();

  return sentencePrefix ? `${normalizedName}: ${sentencePrefix}` : normalizedName;
};

const buildReferenceUrl = (record, definition, language) => {
  if (language === "ru") {
    if (record.origin === "progression") return "";
    const reference = getRussianPassiveReference(record);
    if (!reference) return "";

    return `${reference.page}#:~:text=${encodeURIComponent(reference.anchor)}`;
  }

  const localizedName = definition?.name?.[language] || definition?.name?.en || "";
  const localizedDescription = definition?.description?.[language] || definition?.description?.en || "";

  const pageCatalog = record.origin === "race" ? RACE_REFERENCE_PAGES : CLASS_REFERENCE_PAGES;
  const preferredPage = pageCatalog[language]?.[record.sourceId];
  const fallbackPage = pageCatalog[language === "en" ? "ru" : "en"]?.[record.sourceId];
  const basePage = preferredPage || fallbackPage || "";
  const textFragment = buildTextFragment(localizedName, localizedDescription);

  if (!basePage) return "";
  if (!textFragment) return basePage;

  return `${basePage}#:~:text=${encodeURIComponent(textFragment)}`;
};

const SORCERER_BLOODLINE_FEATURE_IDS = new Set([
  "class-sorcerer-bloodline",
  "class-sorcerer-bloodline-power",
  "class-sorcerer-bloodline-spell",
  "class-sorcerer-bloodline-feat",
]);

const passiveGroupKey = (record) => {
  if (record.origin === "class" && record.sourceId === "sorcerer" && SORCERER_BLOODLINE_FEATURE_IDS.has(record.featureId)) {
    return `${record.origin}:${record.sourceId}:bloodline-family`;
  }
  return `${record.origin}:${record.sourceId}:${record.featureId}`;
};

const passiveGroupKind = (record) => (
  record.origin === "class" && record.sourceId === "sorcerer" && SORCERER_BLOODLINE_FEATURE_IDS.has(record.featureId)
    ? "sorcerer-bloodline"
    : "default"
);

const choiceFeatureId = (choice) => (choice?.id ? String(choice.id).split(":")[2] || "" : "");

const summarizeNumberList = (values) => {
  const unique = [...new Set(values.map((value) => Number(value)).filter((value) => Number.isFinite(value)))].sort((left, right) => left - right);
  if (unique.length === 0) return "";
  const ranges = [];
  let rangeStart = unique[0];
  let previous = unique[0];

  unique.slice(1).forEach((value) => {
    if (value === previous + 1) {
      previous = value;
      return;
    }
    ranges.push(rangeStart === previous ? `${rangeStart}` : `${rangeStart}-${previous}`);
    rangeStart = value;
    previous = value;
  });
  ranges.push(rangeStart === previous ? `${rangeStart}` : `${rangeStart}-${previous}`);
  return ranges.join(", ");
};

const buildSorcererBloodlineDescription = (passive, language) => {
  const records = passive.records || [];
  const bloodlineDefinition = records.find((record) => record.featureId === "class-sorcerer-bloodline");
  const baseDescription = bloodlineDefinition?.description?.[language]
    || bloodlineDefinition?.description?.en
    || passive.description
    || "";
  const powerLevels = records
    .filter((record) => record.featureId === "class-sorcerer-bloodline-power")
    .map((record) => record.sourceLevel);
  const spellCircles = records
    .filter((record) => record.featureId === "class-sorcerer-bloodline-spell")
    .map((record) => {
      const localizedValue = record.value?.[language] || record.value?.en || "";
      const match = localizedValue.match(/\d+/);
      return match ? Number(match[0]) : NaN;
    });
  const bonusFeatCount = passive.choices.filter((choice) => choice.type === "bloodline-feat").length;
  const parts = [baseDescription];
  const powerSummary = summarizeNumberList(powerLevels);
  const spellSummary = summarizeNumberList(spellCircles);

  if (powerSummary) {
    parts.push(language === "en"
      ? `Bloodline powers: character levels ${powerSummary}.`
      : `Способности наследия: ${powerSummary} уровни.`);
  }
  if (spellSummary) {
    parts.push(language === "en"
      ? `Bloodline spells: spell levels ${spellSummary}.`
      : `Заклинания наследия: круги ${spellSummary}.`);
  }
  if (bonusFeatCount > 0) {
    parts.push(language === "en"
      ? `Bloodline bonus feats available: ${bonusFeatCount}.`
      : `Доступно дополнительных черт наследия: ${bonusFeatCount}.`);
  }

  return parts.filter(Boolean).join(" ");
};

const featCatalogName = (featId, language) => featCatalogById.get(featId)?.name?.[language]
  || featCatalogById.get(featId)?.name?.en
  || featId;
const INTERACTIVE_CHOICE_TYPES = new Set([
  "ability",
  "arcane-bond",
  "arcane-school",
  "cleric-domain",
  "divine-bond",
  "favored-class",
  "mercy",
  "favored-enemy",
  "favored-terrain",
  "hunters-bond",
  "nature-bond",
  "performance",
  "rogue-talent",
  "skill",
  "sorcerer-bloodline",
  "weapon-group",
]);
const CHOICE_LABELS = {
  en: {
    act: "Act",
    "animal-companion": "Animal companion",
    companions: "Animal companions",
    party: "Allies",
    comedy: "Comedy",
    cursed: "Cursed",
    "bonded-object": "Bonded object",
    "bonded-weapon": "Bonded weapon",
    blinded: "Blinded",
    mount: "Mount",
    domain: "Domain",
    air: "Air",
    animal: "Animal",
    artifice: "Artifice",
    chaos: "Chaos",
    charm: "Charm",
    community: "Community",
    darkness: "Darkness",
    dazed: "Dazed",
    deafened: "Deafened",
    death: "Death",
    diseased: "Diseased",
    destruction: "Destruction",
    earth: "Earth",
    evil: "Evil",
    exhausted: "Exhausted",
    fatigued: "Fatigued",
    fire: "Fire",
    frightened: "Frightened",
    glory: "Glory",
    good: "Good",
    healing: "Healing",
    knowledge: "Knowledge",
    law: "Law",
    liberation: "Liberation",
    luck: "Luck",
    madness: "Madness",
    magic: "Magic",
    nauseated: "Nauseated",
    nobility: "Nobility",
    paralyzed: "Paralyzed",
    plant: "Plant",
    poisoned: "Poisoned",
    protection: "Protection",
    repose: "Repose",
    rune: "Rune",
    shaken: "Shaken",
    sickened: "Sickened",
    strength: "Strength",
    staggered: "Staggered",
    stunned: "Stunned",
    sun: "Sun",
    travel: "Travel",
    trickery: "Trickery",
    war: "War",
    water: "Water",
    weather: "Weather",
    keyboard: "Keyboard",
    oratory: "Oratory",
    percussion: "Percussion",
    sing: "Sing",
    string: "String",
    wind: "Wind",
    dance: "Dance",
    aberration: "Aberration",
    construct: "Construct",
    dragon: "Dragon",
    fey: "Fey",
    "humanoid-aquatic": "Humanoid (aquatic)",
    "humanoid-dwarf": "Humanoid (dwarf)",
    "humanoid-elf": "Humanoid (elf)",
    "humanoid-giant": "Humanoid (giant)",
    "humanoid-goblinoid": "Humanoid (goblinoid)",
    "humanoid-gnoll": "Humanoid (gnoll)",
    "humanoid-gnome": "Humanoid (gnome)",
    "humanoid-halfling": "Humanoid (halfling)",
    "humanoid-human": "Humanoid (human)",
    "humanoid-orc": "Humanoid (orc)",
    "magical-beast": "Magical beast",
    "monstrous-humanoid": "Monstrous humanoid",
    ooze: "Ooze",
    "outsider-evil": "Outsider (evil)",
    "outsider-lawful": "Outsider (lawful)",
    "outsider-chaotic": "Outsider (chaotic)",
    "outsider-good": "Outsider (good)",
    undead: "Undead",
    vermin: "Vermin",
    archery: "Archery",
    crossbow: "Crossbow",
    "natural-weapon": "Natural weapon",
    "two-weapon": "Two-weapon",
    "weapon-and-shield": "Weapon and shield",
    cold: "Cold",
    desert: "Desert",
    forest: "Forest",
    jungle: "Jungle",
    mountain: "Mountain",
    plains: "Plains",
    planes: "Planes",
    swamp: "Swamp",
    underground: "Underground",
    urban: "Urban",
    familiar: "Familiar",
    abjuration: "Abjuration",
    conjuration: "Conjuration",
    divination: "Divination",
    enchantment: "Enchantment",
    evocation: "Evocation",
    illusion: "Illusion",
    necromancy: "Necromancy",
    transmutation: "Transmutation",
    universalist: "Universalist",
    aberrant: "Aberrant",
    abyssal: "Abyssal",
    arcane: "Arcane",
    celestial: "Celestial",
    destined: "Destined",
    draconic: "Draconic",
    elemental: "Elemental",
    infernal: "Infernal",
    serpentine: "Serpentine",
    axes: "Axes",
    bows: "Bows",
    close: "Close",
    crossbows: "Crossbows",
    double: "Double",
    firearms: "Firearms",
    flails: "Flails",
    hammers: "Hammers",
    "heavy-blades": "Heavy blades",
    "light-blades": "Light blades",
    monk: "Monk",
    natural: "Natural",
    polearms: "Polearms",
    "bleeding-attack": "Bleeding attack",
    camouflage: "Camouflage",
    "combat-trick": "Combat trick",
    "crippling-strike": "Crippling strike",
    edge: "Edge",
    "fast-stealth": "Fast stealth",
    feat: "Feat",
    "finesse-rogue": "Finesse rogue",
    grit: "Grit",
    "hard-to-fool": "Hard to fool",
    "hide-in-plain-sight": "Hide in plain sight",
    "improved-evasion": "Improved evasion",
    "intimidating-glare": "Intimidating glare",
    "ki-pool": "Ki pool",
    "minor-magic": "Minor magic",
    "ninja-trick": "Ninja trick",
    opportunist: "Opportunist",
    resiliency: "Resiliency",
    "rogue-crawl": "Rogue crawl",
    "rogue-talent": "Rogue talent",
    "skill-mastery": "Skill mastery",
    "slow-reactions": "Slow reactions",
    "slippery-mind": "Slippery mind",
    "stand-up": "Stand up",
    "surprise-attack": "Surprise attack",
    spears: "Spears",
    talent: "Another rogue talent",
    "terrain-mastery": "Terrain mastery",
    thrown: "Thrown",
    "trap-spotter": "Trap spotter",
    "unwitting-ally": "Unwitting ally",
    "weapon-training": "Weapon training",
  },
  ru: {
    act: "Лицедейство",
    "animal-companion": "Животное-компаньон",
    companions: "Компаньоны",
    party: "Союзники",
    comedy: "Комедия",
    cursed: "Проклятие",
    "bonded-object": "Связанный предмет",
    "bonded-weapon": "Связанное оружие",
    blinded: "Ослепление",
    mount: "Скакун",
    domain: "Домен",
    air: "Воздух",
    animal: "Животные",
    artifice: "Ремесло",
    chaos: "Хаос",
    charm: "Очарование",
    community: "Сообщество",
    darkness: "Тьма",
    dazed: "Ошеломление",
    deafened: "Глухота",
    death: "Смерть",
    diseased: "Болезнь",
    destruction: "Разрушение",
    earth: "Земля",
    evil: "Зло",
    exhausted: "Изнеможение",
    fatigued: "Усталость",
    fire: "Огонь",
    frightened: "Паника",
    glory: "Слава",
    good: "Добро",
    healing: "Лечение",
    knowledge: "Знание",
    law: "Закон",
    liberation: "Свобода",
    luck: "Удача",
    madness: "Безумие",
    magic: "Магия",
    nauseated: "Тошнота",
    nobility: "Знать",
    paralyzed: "Паралич",
    plant: "Растения",
    poisoned: "Отравление",
    protection: "Защита",
    repose: "Покой",
    rune: "Руны",
    shaken: "Испуг",
    sickened: "Тошнота/болезненность",
    strength: "Сила",
    staggered: "Пошатывание",
    stunned: "Оглушение",
    sun: "Солнце",
    travel: "Путешествие",
    trickery: "Обман",
    war: "Война",
    water: "Вода",
    weather: "Погода",
    keyboard: "Клавишные",
    oratory: "Красноречие",
    percussion: "Ударные",
    sing: "Пение",
    string: "Струнные",
    wind: "Духовые",
    dance: "Танец",
    aberration: "Аберрации",
    construct: "Конструкты",
    dragon: "Драконы",
    fey: "Феи",
    "humanoid-aquatic": "Гуманоиды (водные)",
    "humanoid-dwarf": "Гуманоиды (дварфы)",
    "humanoid-elf": "Гуманоиды (эльфы)",
    "humanoid-giant": "Гуманоиды (великаны)",
    "humanoid-goblinoid": "Гуманоиды (гоблиноиды)",
    "humanoid-gnoll": "Гуманоиды (гноллы)",
    "humanoid-gnome": "Гуманоиды (гномы)",
    "humanoid-halfling": "Гуманоиды (полурослики)",
    "humanoid-human": "Гуманоиды (люди)",
    "humanoid-orc": "Гуманоиды (орки)",
    "magical-beast": "Магические звери",
    "monstrous-humanoid": "Чудовищные гуманоиды",
    ooze: "Слизни",
    "outsider-evil": "Аутсайдеры (злые)",
    "outsider-lawful": "Аутсайдеры (законные)",
    "outsider-chaotic": "Аутсайдеры (хаотичные)",
    "outsider-good": "Аутсайдеры (добрые)",
    undead: "Нежить",
    vermin: "Паразиты",
    archery: "Стрельба",
    crossbow: "Арбалеты",
    "natural-weapon": "Естественное оружие",
    "two-weapon": "Два оружия",
    "weapon-and-shield": "Оружие и щит",
    cold: "Холод",
    desert: "Пустыня",
    forest: "Лес",
    jungle: "Джунгли",
    mountain: "Горы",
    plains: "Равнины",
    planes: "Планы",
    swamp: "Болото",
    underground: "Подземелья",
    urban: "Город",
    familiar: "Фамильяр",
    abjuration: "Ограждение",
    conjuration: "Призыв",
    divination: "Прорицание",
    enchantment: "Очарование",
    evocation: "Воплощение",
    illusion: "Иллюзии",
    necromancy: "Некромантия",
    transmutation: "Преобразование",
    universalist: "Универсалист",
    aberrant: "Аберрантное",
    abyssal: "Бездонное",
    arcane: "Мистическое",
    celestial: "Небесное",
    destined: "Предначертанное",
    draconic: "Драконье",
    elemental: "Стихийное",
    infernal: "Инфернальное",
    serpentine: "Змеиное",
    axes: "Топоры",
    bows: "Луки",
    close: "Ближнее",
    crossbows: "Арбалеты",
    double: "Двустороннее",
    firearms: "Огнестрельное",
    flails: "Цепы",
    hammers: "Молоты",
    "heavy-blades": "Тяжёлые клинки",
    "light-blades": "Лёгкие клинки",
    monk: "Оружие монаха",
    natural: "Естественное",
    polearms: "Древковое",
    "bleeding-attack": "Кровоточащая атака",
    camouflage: "Камуфляж",
    "combat-trick": "Боевой прием",
    "crippling-strike": "Калечащий удар",
    edge: "Чутье",
    "fast-stealth": "Быстрая скрытность",
    feat: "Дополнительная черта",
    "finesse-rogue": "Ловкий плут",
    grit: "Упорство",
    "hard-to-fool": "Трудно провести",
    "hide-in-plain-sight": "Незаметность на виду",
    "improved-evasion": "Уклонение+",
    "intimidating-glare": "Устрашающий взгляд",
    "ki-pool": "Запас ци",
    "minor-magic": "Малая магия",
    "ninja-trick": "Трюк ниндзя",
    opportunist: "Оппортунист",
    resiliency: "Живучесть",
    "rogue-crawl": "Плутовское ползание",
    "rogue-talent": "Воровской прием",
    "skill-mastery": "Мастерство навыков",
    "slow-reactions": "Замедленные реакции",
    "slippery-mind": "Скользкий разум",
    "stand-up": "Быстрый подъем",
    "surprise-attack": "Внезапная атака",
    spears: "Копья",
    talent: "Еще один воровской прием",
    "terrain-mastery": "Мастер местности",
    thrown: "Метательное",
    "trap-spotter": "Искатель ловушек",
    "unwitting-ally": "Невольный союзник",
    "weapon-training": "Тренировка с оружием",
  },
};
const appendUniqueSentence = (baseText, extraText) => {
  const normalizedBase = (baseText || "").trim();
  const normalizedExtra = (extraText || "").trim();
  const genericFallbacks = new Set(["Player choice required.", "Требуется выбор игрока."]);

  if (!normalizedExtra) return normalizedBase;
  if (!normalizedBase) return normalizedExtra;
  if (genericFallbacks.has(normalizedBase) && normalizedExtra !== normalizedBase) return normalizedExtra;
  if (normalizedBase === normalizedExtra || normalizedBase.includes(normalizedExtra)) return normalizedBase;

  return `${normalizedBase} ${normalizedExtra}`;
};
const isPlaceholderChoiceValue = (value) => {
  const normalized = (value || "").trim().toLowerCase();
  return normalized === "choice" || normalized === "выбор";
};
const choiceOptionMeta = (choice, option, language, sourceProgressLevel = choice.sourceLevel) => {
  if (choice.type === "rogue-talent" && sourceProgressLevel < 10 && isAdvancedRogueTalentOption(option)) {
    return {
      disabled: true,
      disabledReason: language === "en"
        ? "Available from rogue level 10."
        : "Доступно с 10-го уровня плута.",
    };
  }
  return { disabled: false, disabledReason: "" };
};

const PassivesAndTraits = ({ grantedFeatures = [], passives, ruleChoices = [], ruleDerived = {}, setRuleChoice = () => {}, setPassives, feats, setFeats }) => {
  const { i18n, t } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language) === "en" ? "en" : "ru";
  const [isPassiveModalOpen, setIsPassiveModalOpen] = useState(false);
  const [isFeatPickerOpen, setIsFeatPickerOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const displayedFeats = useMemo(() => feats.map((feat) => ({ record: feat, catalog: featCatalogById.get(feat.catalogId || feat.id) })), [feats]);
  const featCapacity = Number(ruleDerived.featCapacity) || 0;
  const spentFeats = Number.isFinite(Number(ruleDerived.spentFeats))
    ? Number(ruleDerived.spentFeats)
    : displayedFeats.length;
  const remainingFeats = featCapacity - spentFeats;
  const hasFeatMismatch = remainingFeats !== 0;
  const featStatus = remainingFeats > 0
    ? t("sheet.featsUnderAllocated", { spent: spentFeats, total: featCapacity, remaining: remainingFeats })
    : remainingFeats < 0
      ? t("sheet.featsOverAllocated", { spent: spentFeats, total: featCapacity, excess: Math.abs(remainingFeats) })
      : t("sheet.featsFullyAllocated", { spent: spentFeats, total: featCapacity });

  const savePassive = () => {
    setPassives((current) => formData.id ? current.map((passive) => passive.id === formData.id ? formData : passive) : [...current, { ...formData, id: createId() }]);
    setIsPassiveModalOpen(false);
  };

  const removePassive = (passiveId) => {
    setPassives((current) => current.filter((passive) => passive.id !== passiveId));
  };

  const addFeat = (feat) => {
    if (feats.some((current) => (current.catalogId || current.id) === feat.id)) return;
    setFeats([...feats, { id: createId(), catalogId: feat.id }]);
    setIsFeatPickerOpen(false);
  };

  const removeFeat = (featId) => setFeats(feats.filter((feat) => feat.id !== featId));
  const choiceLabel = (choice, index) => {
    if (choice.type === "ability") return language === "en" ? "Ability score bonus" : "Бонус характеристики";
    if (choice.type === "feat") return language === "en" ? "Bonus feat" : "Дополнительная черта";
    if (choice.type === "skill") return language === "en" ? "Chosen skill" : "Выбранный навык";
    if (choice.type === "favored-class") return language === "en" ? `Favored class ${index + 1}` : `Предпочитаемый класс ${index + 1}`;
    if (choice.type === "performance") return language === "en" ? `Versatile performance ${index + 1}` : `Прикладное искусство ${index + 1}`;
    if (choice.type === "rogue-talent") return language === "en" ? `Rogue talent ${index + 1}` : `Воровской прием ${index + 1}`;
    if (choice.type === "cleric-domain") return language === "en" ? `Domain ${index + 1}` : `Сфера ${index + 1}`;
    if (choice.type === "nature-bond") return language === "en" ? "Nature bond" : "Связь с природой";
    if (choice.type === "weapon-group") return language === "en" ? `Weapon group ${index + 1}` : `Группа оружия ${index + 1}`;
    if (choice.type === "divine-bond") return language === "en" ? "Divine bond" : "Связь с божеством";
    if (choice.type === "mercy") return language === "en" ? `Mercy ${index + 1}` : `Избавление ${index + 1}`;
    if (choice.type === "favored-enemy") return language === "en" ? `Favored enemy ${index + 1}` : `Заклятый враг ${index + 1}`;
    if (choice.type === "favored-terrain") return language === "en" ? `Favored terrain ${index + 1}` : `Излюбленная местность ${index + 1}`;
    if (choice.type === "hunters-bond") return language === "en" ? "Hunter's bond" : "Совместная охота";
    if (choice.type === "sorcerer-bloodline") return language === "en" ? "Bloodline" : "Наследие";
    if (choice.type === "arcane-bond") return language === "en" ? "Arcane bond" : "Мистическая связь";
    if (choice.type === "arcane-school") return language === "en" ? "Arcane school" : "Школа магии";
    return choice.type;
  };
  const choiceOptionLabel = (choice, option) => {
    if (choice.type === "ability") return t(systemLabelKey("abilities", option));
    if (choice.type === "skill") return t(systemLabelKey("skills", option));
    if (choice.type === "favored-class") return t(systemLabelKey("classes", option));
    if (choice.type === "feat") return featCatalogName(option, language);
    return CHOICE_LABELS[language][option] || CHOICE_LABELS.en[option] || option;
  };
  const choicePlaceholder = (choice) => {
    if (choice.type === "feat") return language === "en" ? "Select feat…" : "Выберите черту…";
    if (choice.type === "skill") return language === "en" ? "Select skill…" : "Выберите навык…";
    if (choice.type === "favored-class") return language === "en" ? "Select class…" : "Выберите класс…";
    if (INTERACTIVE_CHOICE_TYPES.has(choice.type)) return language === "en" ? "Select option…" : "Выберите вариант…";
    return language === "en" ? "Select…" : "Выберите…";
  };
  const compactChoiceSummary = (choices) => {
    if (choices.length === 0) return "";
    const featChoiceTypes = new Set(["feat", "combat-feat", "monk-bonus-feat", "wizard-bonus-feat", "bloodline-feat"]);
    const featChoices = choices.filter((choice) => featChoiceTypes.has(choice.type)).length;
    if (featChoices > 0) {
      return language === "en"
        ? `Additional feats available: ${featChoices}.`
        : `Доступно дополнительных черт: ${featChoices}.`;
    }
    const ragePowerChoices = choices.filter((choice) => choice.type === "rage-power").length;
    if (ragePowerChoices > 0) {
      return language === "en"
        ? `Rage powers available: ${ragePowerChoices}.`
        : `Доступно даров ярости: ${ragePowerChoices}.`;
    }
    const rogueTalentChoices = choices.filter((choice) => choice.type === "rogue-talent").length;
    if (rogueTalentChoices > 0) {
      return language === "en"
        ? `Rogue talents available: ${rogueTalentChoices}.`
        : `Доступно воровских приемов: ${rogueTalentChoices}.`;
    }
    return language === "en" ? "Player choice required." : "Требуется выбор игрока.";
  };
  const featureDefinition = (record) => record.origin === "race"
    ? getRaceRules(record.sourceId)?.features?.find((feature) => feature.id === record.featureId)
    : record.origin === "class"
      ? getClassFeature(record.sourceId, record.featureId.replace(`class-${record.sourceId}-`, ""))
      : getCharacterFeature(record.featureId);
  const sourceName = (record) => {
    if (record.origin === "progression") return language === "en" ? "Character level" : "Уровень персонажа";
    return t(systemLabelKey(record.origin === "race" ? "races" : "classes", record.sourceId));
  };
  const effectImpact = (effect) => {
    if (effect.type === "save-bonus" && effect.valueFrom === "ability-modifier") {
      const value = Number(ruleDerived.classFeatureSaveBonuses?.fortitude) || 0;
      return {
        target: t("sheet.impactAllSaves"),
        amount: value > 0 ? `+${value}` : "0",
        bonusType: t("sheet.impactUntypedBonus"),
        condition: t("sheet.impactPositiveCharisma"),
        status: value > 0 ? t("sheet.impactApplied") : t("sheet.impactConditionNotMet"),
        statusKind: value > 0 ? "applied" : "conditional",
      };
    }
    if (effect.type === "skill-bonus" && effect.skillGroup === "knowledge") {
      const value = Number(ruleDerived.skillGroupBonuses?.knowledge) || 0;
      return {
        target: t("sheet.impactKnowledgeChecks"),
        amount: `+${value}`,
        bonusType: t("sheet.impactUntypedBonus"),
        condition: t("sheet.impactKnowledgeUntrained"),
        status: t("sheet.impactApplied"),
        statusKind: "applied",
      };
    }
    if (effect.type === "skill-substitution") {
      return {
        target: t("sheet.impactAssociatedSkills"),
        amount: t("sheet.impactFullPerformBonus"),
        bonusType: t("sheet.impactSubstitution"),
        condition: t("sheet.impactChosenPerformance"),
        status: t("sheet.impactManualConditional"),
        statusKind: "conditional",
      };
    }
    if (effect.type === "choice") {
      return {
        target: t("sheet.impactPlayerChoice"),
        amount: t("sheet.impactSeeDescription"),
        bonusType: t("sheet.impactRuleEffect"),
        condition: t("sheet.impactSelectionRequired"),
        status: t("sheet.impactSelectionStatus"),
        statusKind: "conditional",
      };
    }
    const isConditional = effect.type === "conditional" || effect.conditional || effect.application === "manual";
    const isApplied = ["skill-ranks-per-level", "skill-bonus", "save-bonus", "proficiency", "bonus-feat"].includes(effect.type);
    return {
      target: t("sheet.impactFeatureMechanic"),
      amount: effect.value == null ? t("sheet.impactSeeDescription") : `${Number(effect.value) >= 0 ? "+" : ""}${effect.value}`,
      bonusType: t("sheet.impactRuleEffect"),
      condition: isConditional ? t("sheet.impactSituational") : t("sheet.impactAlways"),
      status: isConditional
        ? t("sheet.impactNotAppliedGlobally")
        : isApplied ? t("sheet.impactApplied") : t("sheet.impactDescriptive"),
      statusKind: isConditional ? "conditional" : isApplied ? "applied" : "descriptive",
    };
  };
  const generatedByKey = new Map();
  const sourceProgressByOrigin = new Map();
  grantedFeatures.forEach((record) => {
    const key = `${record.origin}:${record.sourceId}`;
    const previousLevel = sourceProgressByOrigin.get(key) || 0;
    sourceProgressByOrigin.set(key, Math.max(previousLevel, Number(record.sourceLevel) || 0));
  });
  grantedFeatures.forEach((record) => {
    const definition = featureDefinition(record);
    if (!definition) return;
    const choices = ruleChoices.filter((choice) => {
      if (!choice.active || choice.sourceId !== record.sourceId) return false;
      if (record.origin === "race") return choiceFeatureId(choice) === record.featureId;
      if (choiceFeatureId(choice) !== record.featureId) return false;
      const classFeatureId = record.featureId.replace(`class-${record.sourceId}-`, "");
      const definition = getClassFeature(record.sourceId, classFeatureId);
      if (!definition || definition.stackMode === "repeat") return choice.sourceLevel === record.sourceLevel;
      return true;
    }).map((choice, index) => {
      const sourceProgressLevel = sourceProgressByOrigin.get(`${record.origin}:${record.sourceId}`) || choice.sourceLevel;
      return {
        ...choice,
        label: choiceLabel(choice, choice.choiceIndex ?? index),
        options: choice.options.map((option) => ({
          value: option,
          label: choiceOptionLabel(choice, option),
          ...choiceOptionMeta(choice, option, language, sourceProgressLevel),
        })),
        placeholder: choicePlaceholder(choice),
      };
    });
    const key = passiveGroupKey(record);
    const rawFeatureValue = record.value?.[language] || record.value?.en || "";
    const featureValue = isPlaceholderChoiceValue(rawFeatureValue) ? "" : rawFeatureValue;
    const existing = generatedByKey.get(key);
    if (existing) {
      existing.count += 1;
      existing.choices.push(...choices);
      existing.records.push({ ...record, description: definition.description });
      existing.sourceLevels.push(record.sourceLevel);
      if (featureValue) existing.values.push(featureValue);
      return;
    }
    const baseName = definition.name?.[language] || definition.name?.en || record.featureId;
    generatedByKey.set(key, {
      ...record,
      groupKind: passiveGroupKind(record),
      baseName,
      name: baseName,
      description: definition.description?.[language] || definition.description?.en || "",
      referenceUrl: buildReferenceUrl(record, definition, language),
      sourceName: sourceName(record),
      sourceLevels: [record.sourceLevel],
      count: 1,
      choices,
      records: [{ ...record, description: definition.description }],
      effects: definition.effects || [],
      values: featureValue ? [featureValue] : [],
    });
  });
  const generatedPassives = [...generatedByKey.values()].map((passive) => {
    const interactiveChoices = passive.choices.filter((choice) => INTERACTIVE_CHOICE_TYPES.has(choice.type));
    const hiddenChoices = passive.choices.filter((choice) => !INTERACTIVE_CHOICE_TYPES.has(choice.type));
    const baseDescription = passive.values.length > 1
      ? `${passive.description}${passive.description ? " " : ""}${passive.values.join("; ")}`
      : passive.description;
    const hiddenSummary = compactChoiceSummary(hiddenChoices);
    const description = passive.groupKind === "sorcerer-bloodline"
      ? buildSorcererBloodlineDescription({ ...passive, choices: hiddenChoices }, language)
      : appendUniqueSentence(baseDescription, hiddenSummary);
    return {
      ...passive,
      name: passive.groupKind === "sorcerer-bloodline"
        ? passive.baseName
        : passive.count > 1
        ? `${passive.baseName} ×${passive.count}`
        : `${passive.baseName}${passive.values[0] ? ` (${passive.values[0]})` : ""}`,
      description,
      impact: {
        label: t("sheet.impactDisclosure"),
        descriptionLabel: t("sheet.impactDescription"),
        sourceLabel: t("sheet.impactSource"),
        targetLabel: t("sheet.impactTarget"),
        amountLabel: t("sheet.impactAmount"),
        typeLabel: t("sheet.impactType"),
        conditionLabel: t("sheet.impactCondition"),
        statusLabel: t("sheet.impactStatus"),
        source: passive.sourceName,
        description,
        effects: (passive.effects.length ? passive.effects : [{ type: "descriptive" }]).map(effectImpact),
      },
      choices: interactiveChoices.map((choice, index) => ({
        ...choice,
        label: choice.options.length
          ? choiceLabel(choice, index)
          : (language === "en" ? `Choice ${index + 1} used` : `Выбор ${index + 1} использован`),
      })),
      hiddenChoicesCount: hiddenChoices.length,
      hiddenChoicesSummary: hiddenSummary,
      source: passive.count > 1
        ? passive.sourceName
        : `${passive.sourceName}${passive.sourceLevel ? ` · ${passive.sourceLevel}` : ""}`,
      choicesSummary: language === "en"
        ? `Choices completed: ${interactiveChoices.filter((choice) => choice.used || choice.selectedValue).length} of ${interactiveChoices.length}`
        : `Заполнено выборов: ${interactiveChoices.filter((choice) => choice.used || choice.selectedValue).length} из ${interactiveChoices.length}`,
    };
  }).sort((left, right) => {
    const leftLevel = Math.min(...left.sourceLevels);
    const rightLevel = Math.min(...right.sourceLevels);
    return leftLevel - rightLevel
      || left.origin.localeCompare(right.origin)
      || left.sourceName.localeCompare(right.sourceName)
      || left.baseName.localeCompare(right.baseName);
  });

  return (
    <div className="passive-skills">
      <PassivesSkillBox
        generatedPassives={generatedPassives}
        passives={passives}
        header={t("sheet.passives")}
        onAddClick={() => { setFormData({ name: "", description: "" }); setIsPassiveModalOpen(true); }}
        onEditClick={(passive) => { setFormData(passive); setIsPassiveModalOpen(true); }}
        onRemoveClick={removePassive}
        onRuleChoice={setRuleChoice}
      />
        <div className="character-passive-skills">
          <div className="skills-header">
            <h2>{t("sheet.feats")}</h2>
            <div className="skills-header-actions">
              <span
                className={`skill-rank-counter${hasFeatMismatch ? " skill-rank-counter-warning" : ""}`}
                role="status"
                aria-label={featStatus}
                title={featStatus}
              >
                <span>{spentFeats} / {featCapacity}</span>
                {hasFeatMismatch && <span className="skill-rank-warning-mark" aria-hidden="true">!</span>}
              </span>
              <button className="add-skill-button" type="button" onClick={() => setIsFeatPickerOpen(true)}>+</button>
            </div>
          </div>
          {displayedFeats.map(({ record, catalog }) => {
          const name = catalog?.name?.[language] || record.name || t("sheet.unknownFeat");
          const description = catalog?.description?.[language] || record.description || "";
          const referenceUrl = catalog?.referenceUrls?.[language]
            || catalog?.referenceUrls?.[language === "en" ? "ru" : "en"];

          return (
            <div key={record.id} className="passive">
              <div className="passive-name">{referenceUrl ? <a href={referenceUrl} target="_blank" rel="noreferrer">{name}</a> : name}</div>
              <div className="passive-description">{description}</div>
              {!record.generated && <button aria-label={t("sheet.removeFeat", { name })} className="remove-feat-button" title={t("common.delete")} type="button" onClick={() => removeFeat(record.id)}>&times;</button>}
            </div>
          );
        })}
      </div>
      <AddPassiveModal isOpen={isPassiveModalOpen} onClose={() => setIsPassiveModalOpen(false)} onSubmit={savePassive} formData={formData} setFormData={setFormData} />
      <FeatPickerModal isOpen={isFeatPickerOpen} onClose={() => setIsFeatPickerOpen(false)} selectedFeats={feats} onSelect={addFeat} />
    </div>
  );
};

export default PassivesAndTraits;
